import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TogglePinPostDocument,
  TogglePinPostMutation,
  TogglePinPostMutationVariables,
  Post,
} from '../../../../generated/graphql';
import { communityQueryKeys } from '../constants';
import { toast } from 'react-hot-toast';
import { apolloClient } from '@client/api/graphql/client';
import { trackEvent } from '@services/tracking';

type TogglePinParams = {
  postId: string;
  pinned: boolean;
  post: Post;
};

export const useTogglePinPost = (communityId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean,
    unknown,
    TogglePinParams,
    { previousPinnedPosts: Post[] | undefined }
  >({
    mutationFn: async ({ postId, pinned }) => {
      const variables: TogglePinPostMutationVariables = {
        communityId,
        postId,
        pinned,
      };

      const response = await apolloClient.mutate<
        TogglePinPostMutation,
        TogglePinPostMutationVariables
      >({
        mutation: TogglePinPostDocument,
        variables,
      });

      return response.data?.togglePinPost ?? false;
    },
    onMutate: async ({ postId, pinned, post }) => {
      await queryClient.cancelQueries({
        queryKey: communityQueryKeys.pinnedPosts(communityId),
      });

      const previousPinnedPosts = queryClient.getQueryData<Post[]>(
        communityQueryKeys.pinnedPosts(communityId),
      );

      if (pinned) {
        queryClient.setQueryData<Post[]>(
          communityQueryKeys.pinnedPosts(communityId),
          (old) => (old ? [post, ...old] : [post]),
        );
      } else {
        queryClient.setQueryData<Post[]>(
          communityQueryKeys.pinnedPosts(communityId),
          (old) => old?.filter((p) => p.id !== postId) ?? [],
        );
      }

      return { previousPinnedPosts };
    },
    onSuccess: (_, { pinned }) => {
      toast.success(pinned ? 'Post pinned' : 'Post unpinned');

      trackEvent({
        type: pinned ? 'Post pinned' : 'Post unpinned',
      });
    },
    onError: (_error, { pinned }, context) => {
      if (context?.previousPinnedPosts) {
        queryClient.setQueryData(
          communityQueryKeys.pinnedPosts(communityId),
          context.previousPinnedPosts,
        );
      }
      toast.error(pinned ? 'Failed to pin post' : 'Failed to unpin post');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: communityQueryKeys.pinnedPosts(communityId),
      });
    },
  });
};
